import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  getPaypalExpressRedirectOrderIdFromSearch,
  hasPaypalExpressFailedRedirectReturnSearch,
  isPaypalExpressRedirectReturn,
  shouldAbandonExpressOrder,
  shouldRecoverPaypalExpressOnPageShow,
} from '@/components/PaypalExpressButton/helpers';
import { APOLLO_CLIENTS } from '@/graphql/client';
import { useOrderAbandonMutation } from '@/graphql/orderApp/api';
import { paypalExpressRedirectRecoveryRef } from '@/hooks/paypalExpressRedirectRef';
import { useCheckout } from '@/hooks/useCheckout';
import { useRouter } from '@/hooks/useRouter';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { abandonPayment, fetchOrderData } from '@/lib/order';
import { PAYPAL_EXPRESS_QUERY_PARAMS } from '@/lib/payment/queryParams';
import { getCurrentUrl } from '@/lib/util';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions } from '@/states/checkout';

export { paypalExpressRedirectRecoveryRef };

const PAYPAL_EXPRESS_REVIEW_PATH = '/store/checkout/review-express';

const isPaypalExpressReviewPath = (path: string): boolean =>
  path.includes(PAYPAL_EXPRESS_REVIEW_PATH);

export const usePaypalExpressRedirectRecovery = () => {
  const { asPath, query, replace } = useRouter();
  const { formatMessage } = useIntl();
  const {
    query: { languageCode },
  } = useRegionProvider();
  const {
    endProcessing,
    snapshot: { checkout },
  } = useCheckout();
  const checkoutId = checkout?.id;

  const [orderAbandon] = useOrderAbandonMutation({
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  const errorHandler = genericErrorHandler({
    formatMessage,
    apiErrorsHandler: checkoutActions.setErrors,
    errorType: 'adyen',
  });

  const recoverRef = useRef<
    (orderIdParam: string, isCancelled?: () => boolean) => Promise<void>
  >(() => Promise.resolve());

  recoverRef.current = async (
    orderIdParam: string,
    isCancelled?: () => boolean,
  ) => {
    if (paypalExpressRedirectRecoveryRef.current || !checkoutId) return;
    paypalExpressRedirectRecoveryRef.current = true;

    const { data } = await fetchOrderData(orderIdParam, languageCode);

    if (shouldAbandonExpressOrder(data?.order)) {
      const res = await orderAbandon({
        variables: { checkoutId, orderId: orderIdParam },
      });

      if (res.errors?.length) {
        errorHandler([{ code: 'PAYMENT_ERROR' }], { errorType: 'orderApp' });
        endProcessing();
        return;
      }

      checkoutActions.setOrderId('');
    } else {
      abandonPayment({ transaction_id: undefined });
    }

    if (isCancelled?.()) return;

    const url = getCurrentUrl();
    replace(url, url, { shallow: true });
  };

  useEffect(() => {
    let isInitialPageShow = true;

    const onPageShow = (event: PageTransitionEvent) => {
      if (isPaypalExpressReviewPath(window.location.pathname)) return;

      const search = window.location.search;
      const hasFailedRedirectReturn =
        hasPaypalExpressFailedRedirectReturnSearch(search);
      const shouldRecover = shouldRecoverPaypalExpressOnPageShow(
        isInitialPageShow,
        event.persisted,
        false,
        hasFailedRedirectReturn,
      );
      isInitialPageShow = false;

      if (!shouldRecover || !hasFailedRedirectReturn) return;

      const orderIdParam = getPaypalExpressRedirectOrderIdFromSearch(search);
      if (orderIdParam) {
        void recoverRef.current(orderIdParam);
      }
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (isPaypalExpressReviewPath(asPath)) return;
    if (!checkoutId || !isPaypalExpressRedirectReturn(query)) return;

    const orderIdParam = query[PAYPAL_EXPRESS_QUERY_PARAMS.orderId];
    if (!orderIdParam || Array.isArray(orderIdParam)) return;

    if (paypalExpressRedirectRecoveryRef.current) return;

    let cancelled = false;
    void recoverRef.current(orderIdParam, () => cancelled);

    return () => {
      cancelled = true;
    };
  }, [asPath, checkoutId, query]);
};
